import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FullscreenIcon from 'material-ui/svg-icons/navigation/fullscreen';
import FullscreenExitIcon from 'material-ui/svg-icons/navigation/fullscreen-exit';

import { cellCenter, cellLatLngs, DEFAULT_H3_RESOLUTION, latLngToCell, pointInGeometry } from './h3Selection';

// Same tile server the ShipNext frontend uses (BaseMap.js), light style to match the admin theme.
const TILE_URL = 'https://maps.shipnext.com/styles/light-v1/256/{z}/{x}/{y}.webp?v=1';
const TILE_ATTR = '&copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>';

export const CELL_COLORS = {
    1: { fill: '#f2a60d', border: '#b57708' }, // At Anchor -> anchorage
    5: { fill: '#1e9be2', border: '#12699b' }, // Moored -> port
};
const SELECTED_COLOR = '#00bcd4'; // admin panel accent (MUI cyan)
const TRACK_STATUS_COLORS = { 0: '#90a4ae', 1: '#f2a60d', 5: '#1e9be2' };
const METERS_PER_NM = 1852;
const RADIUS_COLOR = '#546e7a';
const TERMINAL_COLOR = '#4527a0';
const BERTH_COLOR = '#00695c';
const BRUSH_STEP_METERS = 80;

function cellStyle(feature, selectedZoneIds, maxHit) {
    const props = feature.properties || {};
    const inSelectedZone = selectedZoneIds && selectedZoneIds.length
        && selectedZoneIds.indexOf(String(props.zoneId || '')) !== -1;
    const colors = CELL_COLORS[props.navStatus] || CELL_COLORS[1];
    const heat = maxHit > 0 ? (props.hitCount || 0) / maxHit : 0;

    return {
        color: inSelectedZone ? SELECTED_COLOR : colors.border,
        weight: inSelectedZone ? 2 : (props.state === 'confirmed' ? 1.8 : 1),
        dashArray: props.state === 'candidate' ? '4 3' : null,
        fillColor: colors.fill,
        fillOpacity: inSelectedZone ? 0.4 : 0.15 + (0.6 * heat),
    };
}

function cellTooltip(props) {
    return `<div class="congestion-tip"><b>${props.cellAddress}</b><br/>`
        + `navStatus ${props.navStatus} (${props.navStatus === 1 ? 'At Anchor' : 'Moored'})<br/>`
        + `state: <b>${props.state}</b>${props.zoneType ? ` &middot; zone: ${props.zoneType}` : ''}<br/>`
        + `stops: <b>${props.hitCount}</b> &middot; points: ${props.pointCount}</div>`;
}

function selectedZoneSet(selectedZoneIds) {
    const set = {};
    (selectedZoneIds || []).forEach((id) => { set[String(id)] = true; });
    return set;
}

function isCellInArea(address, map, portInfo, selectionRadiusNm, existingAddresses) {
    const center = cellCenter(address);
    if (portInfo && portInfo.point && selectionRadiusNm) {
        const [lng, lat] = portInfo.point.coordinates;
        return map.distance([center.lat, center.lng], [lat, lng]) <= selectionRadiusNm * METERS_PER_NM;
    }
    if (portInfo && portInfo.scanPolygon) {
        return pointInGeometry(center.lng, center.lat, portInfo.scanPolygon);
    }
    return !!(existingAddresses && existingAddresses[address]);
}

function paintSelection(layer, painted, addresses) {
    const wanted = {};
    addresses.forEach((address) => { wanted[address] = true; });
    Object.keys(painted).forEach((address) => {
        if (!wanted[address]) {
            layer.removeLayer(painted[address]);
            delete painted[address];
        }
    });
    addresses.forEach((address) => {
        if (painted[address]) return;
        try {
            painted[address] = L.polygon(cellLatLngs(address), {
                color: SELECTED_COLOR,
                weight: 2,
                fillColor: SELECTED_COLOR,
                fillOpacity: 0.45,
                interactive: false,
            }).addTo(layer);
        } catch (error) {
            // invalid H3 index from a bad sample — skip it
        }
    });
}

/**
 * Leaflet renderer. Hit cells come from the API; empty hexes inside the scan area
 * are created on the client only while brush-selecting.
 */
export default function CongestionMap({
    portInfo,
    selectionRadiusNm,
    cellsFeatures = [],
    zonesFeatures = [],
    selectedAddresses = [],
    selectedZoneIds = [],
    drawEnabled = false,
    showTerminals = false,
    onSelectionChange,
    track,
    scrubTs,
}) {
    const wrapRef = useRef(null);
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const [fullscreen, setFullscreen] = useState(false);
    const layersRef = useRef({});
    const selectedRef = useRef(selectedAddresses);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const areaRef = useRef({ portInfo, selectionRadiusNm });
    const existingRef = useRef({});
    const resolutionRef = useRef(DEFAULT_H3_RESOLUTION);
    const brushRef = useRef(null);
    const drawEnabledRef = useRef(drawEnabled);

    selectedRef.current = selectedAddresses;
    onSelectionChangeRef.current = onSelectionChange;
    drawEnabledRef.current = drawEnabled;
    areaRef.current = { portInfo, selectionRadiusNm };
    existingRef.current = {};
    cellsFeatures.forEach((feature) => {
        const address = feature.properties && feature.properties.cellAddress;
        if (address) existingRef.current[address] = true;
    });
    resolutionRef.current = (cellsFeatures[0] && cellsFeatures[0].properties && cellsFeatures[0].properties.resolution)
        || DEFAULT_H3_RESOLUTION;

    useEffect(() => {
        const map = L.map(containerRef.current, { preferCanvas: true, boxZoom: false }).setView([30, 0], 2);
        L.tileLayer(TILE_URL, { attribution: TILE_ATTR, tileSize: 256 }).addTo(map);
        layersRef.current = {
            port: L.layerGroup().addTo(map),
            zones: L.layerGroup().addTo(map),
            cells: L.layerGroup().addTo(map),
            terminals: L.layerGroup().addTo(map),
            selection: L.layerGroup().addTo(map),
            track: L.layerGroup().addTo(map),
            painted: {},
            marker: null,
        };
        mapRef.current = map;

        const finishBrush = () => {
            const brush = brushRef.current;
            if (!brush) return;
            brushRef.current = null;
            window.removeEventListener('mousemove', onWindowMove);
            window.removeEventListener('mouseup', finishBrush);
            map.dragging.enable();
            const next = Object.keys(brush.selected);
            if (onSelectionChangeRef.current) onSelectionChangeRef.current(next);
        };

        const applyAddress = (address) => {
            const brush = brushRef.current;
            if (!brush || !address) return;
            if (!isCellInArea(address, map, areaRef.current.portInfo, areaRef.current.selectionRadiusNm, existingRef.current)) {
                return;
            }
            if (brush.mode === 'add') brush.selected[address] = true;
            else delete brush.selected[address];
            paintSelection(layersRef.current.selection, layersRef.current.painted, Object.keys(brush.selected));
        };

        const sampleTo = (latlng) => {
            const brush = brushRef.current;
            if (!brush) return;
            const from = brush.lastLatLng;
            const distance = from ? map.distance(from, latlng) : 0;
            const steps = Math.max(1, Math.ceil(distance / BRUSH_STEP_METERS));
            for (let i = 1; i <= steps; i += 1) {
                const fraction = i / steps;
                const lat = from.lat + ((latlng.lat - from.lat) * fraction);
                const lng = from.lng + ((latlng.lng - from.lng) * fraction);
                applyAddress(latLngToCell(lat, lng, resolutionRef.current));
            }
            brush.lastLatLng = latlng;
        };

        const onWindowMove = (event) => {
            if (!brushRef.current) return;
            sampleTo(map.mouseEventToLatLng(event));
        };

        map.on('mousedown', (event) => {
            if (event.originalEvent.button !== 0 || !drawEnabledRef.current) return;
            const address = latLngToCell(event.latlng.lat, event.latlng.lng, resolutionRef.current);
            if (!isCellInArea(address, map, areaRef.current.portInfo, areaRef.current.selectionRadiusNm, existingRef.current)) {
                return;
            }
            L.DomEvent.preventDefault(event.originalEvent);
            map.dragging.disable();
            const selected = {};
            (selectedRef.current || []).forEach((item) => { selected[item] = true; });
            brushRef.current = {
                mode: selected[address] ? 'remove' : 'add',
                selected,
                lastLatLng: event.latlng,
            };
            applyAddress(address);
            window.addEventListener('mousemove', onWindowMove);
            window.addEventListener('mouseup', finishBrush);
        });

        return () => {
            finishBrush();
            map.remove();
        };
    }, []);

    // the map keeps its old pixel size unless told the container changed
    useEffect(() => {
        const onChange = () => {
            setFullscreen(document.fullscreenElement === wrapRef.current);
            const map = mapRef.current;
            if (!map) return;
            map.invalidateSize();
            window.setTimeout(() => map.invalidateSize(), 200);
        };
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
            return;
        }
        if (wrapRef.current && wrapRef.current.requestFullscreen) {
            wrapRef.current.requestFullscreen();
        }
    };

    // port point + scan polygon / radius; recenter when the port or scan area changes
    useEffect(() => {
        const map = mapRef.current;
        const layer = layersRef.current.port;
        if (!map || !layer) return;
        layer.clearLayers();
        if (!portInfo) return;

        let bounds = null;
        if (portInfo.scanPolygon) {
            const outline = L.geoJSON(portInfo.scanPolygon, {
                style: { color: '#78909c', weight: 1.5, dashArray: '6 5', fill: false },
            }).addTo(layer);
            outline.bindTooltip('port scan polygon', { sticky: true });
            bounds = outline.getBounds();
        }
        if (portInfo.point) {
            const [lng, lat] = portInfo.point.coordinates;
            L.circleMarker([lat, lng], {
                radius: 6, color: '#ffffff', weight: 2, fillColor: SELECTED_COLOR, fillOpacity: 1,
            }).addTo(layer).bindTooltip(`${portInfo.name} (${portInfo.unLoCode || 'no code'})`);

            if (selectionRadiusNm) {
                const circle = L.circle([lat, lng], {
                    radius: selectionRadiusNm * METERS_PER_NM,
                    color: RADIUS_COLOR,
                    weight: 1.5,
                    dashArray: '2 6',
                    fill: false,
                }).addTo(layer);
                circle.bindTooltip(`scan radius ${selectionRadiusNm} nm`, { sticky: true });
                const circleBounds = circle.getBounds();
                bounds = bounds ? bounds.extend(circleBounds) : circleBounds;
            }

            if (!bounds) map.setView([lat, lng], 11);
        }
        if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
        }
    }, [portInfo, selectionRadiusNm]);

    // terminals and berths from the port's own map; above the hexes so the quay outlines stay readable
    useEffect(() => {
        const layer = layersRef.current.terminals;
        if (!layer) return;
        layer.clearLayers();
        const features = (showTerminals && portInfo && portInfo.terminals && portInfo.terminals.features) || [];
        if (!features.length) return;

        const terminalNames = {};
        features.forEach((feature) => {
            const props = feature.properties || {};
            if (props.terminalID && !props.berthID && props.name) terminalNames[props.terminalID] = props.name;
        });

        // Terminals first, so a berth drawn inside one sits on top of it.
        const ordered = features.slice().sort((a, b) => {
            const aBerth = a.properties && a.properties.berthID ? 1 : 0;
            const bBerth = b.properties && b.properties.berthID ? 1 : 0;
            return aBerth - bBerth;
        });

        L.geoJSON({ type: 'FeatureCollection', features: ordered }, {
            bubblingMouseEvents: true,
            style: (feature) => {
                const berth = feature.properties && feature.properties.berthID;
                return berth
                    ? {
                        color: BERTH_COLOR,
                        weight: 1.5,
                        fillColor: '#00897b',
                        fillOpacity: 0.28,
                    }
                    : {
                        color: TERMINAL_COLOR,
                        weight: 2,
                        dashArray: '5 4',
                        fillColor: '#5e35b1',
                        fillOpacity: 0.1,
                    };
            },
            onEachFeature: (feature, polygon) => {
                const props = feature.properties || {};
                const terminalName = props.terminalID && terminalNames[props.terminalID];
                const type = props.type ? ` (${props.type})` : '';
                const label = props.berthID
                    ? `berth: <b>${props.name || 'unnamed'}</b>${type}${terminalName ? ` &middot; ${terminalName}` : ''}`
                    : `terminal: <b>${props.name || 'unnamed'}</b>${type}`;
                polygon.bindTooltip(label, { sticky: true });
            },
        }).addTo(layer);
    }, [portInfo, showTerminals]);

    // hex cells — selection is a separate overlay so restyling this layer does not drop clicks
    useEffect(() => {
        const layer = layersRef.current.cells;
        if (!layer) return;
        layer.clearLayers();
        if (!cellsFeatures.length) return;

        const maxHit = cellsFeatures.reduce((top, feature) => Math.max(top, (feature.properties || {}).hitCount || 0), 0);
        L.geoJSON({ type: 'FeatureCollection', features: cellsFeatures }, {
            style: feature => cellStyle(feature, selectedZoneIds, maxHit),
            onEachFeature: (feature, cellLayer) => {
                cellLayer.bindTooltip(cellTooltip(feature.properties || {}), { sticky: true });
            },
        }).addTo(layer);
    }, [cellsFeatures, selectedZoneIds]);

    useEffect(() => {
        const layer = layersRef.current.selection;
        const painted = layersRef.current.painted;
        if (!layer || !painted || brushRef.current) return;
        paintSelection(layer, painted, selectedAddresses);
    }, [selectedAddresses]);

    // confirmed zones (clusters of cells) as thicker outlines — not interactive, so hex clicks work
    useEffect(() => {
        const layer = layersRef.current.zones;
        if (!layer) return;
        layer.clearLayers();
        if (!zonesFeatures.length) return;

        const selected = selectedZoneSet(selectedZoneIds);
        L.geoJSON({ type: 'FeatureCollection', features: zonesFeatures }, {
            interactive: false,
            style: (feature) => {
                const props = feature.properties || {};
                const colors = CELL_COLORS[props.navStatus] || CELL_COLORS[1];
                const isSelected = selected[String(props._id || feature.id || '')];
                return {
                    color: isSelected ? SELECTED_COLOR : colors.border,
                    weight: isSelected ? 4 : 3,
                    fillColor: isSelected ? SELECTED_COLOR : colors.fill,
                    fillOpacity: isSelected ? 0.22 : 0.06,
                    interactive: false,
                };
            },
            onEachFeature: (feature, zoneLayer) => {
                const props = feature.properties || {};
                const label = props.name || props.zoneType;
                zoneLayer.bindTooltip(
                    `zone: <b>${label}</b> &middot; ${props.zoneType} &middot; ${props.cellCount} cells`,
                    { sticky: true },
                );
            },
        }).addTo(layer);
    }, [zonesFeatures, selectedZoneIds]);

    // vessel track + playback marker
    useEffect(() => {
        const layer = layersRef.current.track;
        if (!layer) return;
        layer.clearLayers();
        layersRef.current.marker = null;
        if (!track || !track.points || !track.points.length) return;

        if (track.lineString) {
            L.geoJSON(track.lineString, { style: { color: '#455a64', weight: 2, opacity: 0.7 } }).addTo(layer);
        }
        track.points.forEach((point) => {
            const [lng, lat] = point.coordinates;
            const color = TRACK_STATUS_COLORS[point.navStatus] || TRACK_STATUS_COLORS[0];
            L.circleMarker([lat, lng], {
                radius: 3, color, fillColor: color, fillOpacity: 0.85, weight: 1,
            }).addTo(layer).bindTooltip(`${new Date(point.ts).toISOString().slice(0, 16).replace('T', ' ')} UTC &middot; status ${point.navStatus}`);
        });
        (track.events || []).forEach((event) => {
            const point = track.points.find(p => p.cellAddress && p.cellAddress === event.cellAddress);
            if (!point) return;
            const [lng, lat] = point.coordinates;
            L.circleMarker([lat, lng], { radius: 7, color: '#37474f', fill: false, weight: 2.5 })
                .addTo(layer)
                .bindTooltip(`${event.type} &middot; ${new Date(event.ts).toISOString().slice(0, 16).replace('T', ' ')} UTC`);
        });

        const bounds = L.latLngBounds(track.points.map(p => [p.coordinates[1], p.coordinates[0]]));
        if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [30, 30] });
    }, [track]);

    // scrubber marker: interpolate the vessel position for the selected moment
    useEffect(() => {
        const map = mapRef.current;
        const layer = layersRef.current.track;
        if (!map || !layer) return;
        if (!track || !track.points || !track.points.length || !scrubTs) {
            if (layersRef.current.marker) {
                layer.removeLayer(layersRef.current.marker);
                layersRef.current.marker = null;
            }
            return;
        }

        const points = track.points;
        let index = 0;
        while (index < points.length - 1 && new Date(points[index + 1].ts).getTime() <= scrubTs) index += 1;
        const current = points[index];
        const next = points[Math.min(index + 1, points.length - 1)];
        const currentTs = new Date(current.ts).getTime();
        const nextTs = new Date(next.ts).getTime();
        const fraction = nextTs > currentTs ? Math.min(1, Math.max(0, (scrubTs - currentTs) / (nextTs - currentTs))) : 0;
        const lat = current.coordinates[1] + ((next.coordinates[1] - current.coordinates[1]) * fraction);
        const lng = current.coordinates[0] + ((next.coordinates[0] - current.coordinates[0]) * fraction);
        const color = TRACK_STATUS_COLORS[current.navStatus] || TRACK_STATUS_COLORS[0];

        if (!layersRef.current.marker) {
            layersRef.current.marker = L.circleMarker([lat, lng], {
                radius: 8, color: '#263238', weight: 2.5, fillColor: color, fillOpacity: 1,
            }).addTo(layer);
        } else {
            layersRef.current.marker.setLatLng([lat, lng]);
            layersRef.current.marker.setStyle({ fillColor: color });
        }
    }, [track, scrubTs]);

    return (
        <div ref={wrapRef} className='congestion-map-wrap'>
            <div ref={containerRef} className='congestion-map' />
            <button
                type='button'
                className='congestion-map-fullscreen'
                aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                onClick={toggleFullscreen}
            >
                {fullscreen
                    ? <FullscreenExitIcon color='#3c4043' hoverColor='#202124' />
                    : <FullscreenIcon color='#3c4043' hoverColor='#202124' />}
            </button>
            <div className='congestion-legend'>
                <b>Legend</b>
                <div><span className='sw' style={{ background: CELL_COLORS[1].fill }} />At Anchor (1) &mdash; anchorage</div>
                <div><span className='sw' style={{ background: CELL_COLORS[5].fill }} />Moored (5) &mdash; port</div>
                <div><span className='sw sw-dashed' />candidate (awaiting review)</div>
                <div><span className='sw sw-solid' />confirmed</div>
                <div><span className='sw sw-dotted' />scan radius</div>
                <div><span className='sw' style={{ background: SELECTED_COLOR }} />selected</div>
                {showTerminals && (
                    <div><span className='sw sw-dashed' style={{ borderColor: TERMINAL_COLOR }} />terminal</div>
                )}
                {showTerminals && (
                    <div><span className='sw' style={{ background: '#00897b', borderColor: BERTH_COLOR }} />berth</div>
                )}
                <div className='congestion-legend-hint'>
                    {drawEnabled ? 'drag inside the radius to brush-select' : 'turn on Draw to brush-select cells'}
                </div>
            </div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/Congestion/CongestionMap.jsx